import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Renueva la sesion contra `POST /api/v1/sesion/refrescar`.
 *
 * El access token dura **DIEZ MINUTOS**, asi que esto no es una optimizacion:
 * sin renovacion automatica el usuario se queda tieso a media pantalla.
 *
 * No recibe ni devuelve tokens: el refresh viaja en su cookie `HttpOnly` —que el
 * backend lee cuando el cuerpo viene vacio— y la respuesta rota las dos cookies.
 */
@Injectable({ providedIn: 'root' })
export class RefrescoSesionService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/api/v1/sesion/refrescar`;

  /**
   * Refresco en vuelo, o `null`.
   *
   * ⚠️ Sin esto, tres peticiones que caducan a la vez lanzan tres refrescos. El
   * refresco es **de un solo uso** y el backend lo ROTA, asi que el segundo y el
   * tercero llegan con una cookie ya consumida: **expulsan al usuario que se
   * acababa de renovar** y ademas disparan la deteccion de reuso del servidor,
   * o sea que ensucian una alarma de robo real.
   */
  private enCurso: Observable<void> | null = null;

  /**
   * Todas las llamadas concurrentes comparten la misma peticion.
   *
   * ⚠️ EL CUERPO VA `null`, NO `{}`. Medido contra el backend: `{}` devuelve
   * **400 — "The RefreshToken field is required."**, porque `[Required]` se
   * valida ANTES del cuerpo del controlador, que es donde vive el respaldo por
   * cookie. Sin cuerpo, `EmptyBodyBehavior.Allow` deja pasar la peticion con
   * `request == null` y el backend lee la cookie.
   *
   * El sintoma habria sido devastador y silencioso: cada 10 minutos el refresco
   * fallaria con 400, el interceptor lo tomaria por "no se pudo renovar" y
   * expulsaria al usuario a media pantalla.
   */
  refrescar(): Observable<void> {
    this.enCurso ??= this.http.post<unknown>(this.url, null).pipe(
      map(() => undefined),
      // Se limpia en los dos desenlaces para que un fallo no deje el servicio
      // creyendo que hay un refresco vivo para siempre.
      tap({
        complete: () => (this.enCurso = null),
        error: () => (this.enCurso = null),
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.enCurso;
  }
}
